'use client';

import type { ElementType } from 'react';

import React, { forwardRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

interface ParagraphProps {
  children?: React.ReactNode;
  innerHtml?: string;
  className?: string;
  size?: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  tag?: ElementType;
  color?: 'white' | 'muted-foreground' | 'muted' | 'black' | 'primary' | 'foreground';
  tracking?: boolean;
  onClick?: () => void;
}

export const Paragraph = forwardRef<HTMLDivElement, ParagraphProps>(
  (
    {
      children,
      innerHtml,
      className,
      size = 'lg',
      color = 'black',
      tag: Tag = 'div',
      onClick,
      tracking = true,
    },
    ref,
  ) => {
    if (innerHtml) {
      return (
        <HtmlRendererRaw
          as={Tag}
          ref={ref}
          onClick={onClick}
          className={cn(
            'transition-all',
            'ea-paragraph__inner-html',
            `ea-paragraph__inner-${size}`,
            `ea-paragraph__inner-${color}`,
            onClick && 'cursor-pointer text-opacity-80',
            tracking && '-tracking-tighter',
            className,
          )}
          html={innerHtml}
        />
      );
    }

    return (
      <Tag
        ref={ref}
        onClick={onClick}
        className={cn(
          'ea-paragraph',
          'transition-all',
          tracking && '-tracking-tighter',
          size === 'sm' && 'text-xs sm:text-sm',
          size === 'base' && 'text-sm sm:text-base',
          size === 'lg' && 'text-sm leading-[140%] sm:text-base sm:leading-[140%]',
          size === 'xl' && 'text-lg lg:text-xl',
          size === '2xl' && 'text-lg sm:text-xl lg:text-2xl',
          color === 'white' && 'text-white',
          color === 'muted-foreground' && 'text-[#666666]',
          color === 'black' && 'text-black',
          color === 'muted' && 'text-muted',
          color === 'foreground' && 'text-foreground',
          color === 'primary' && 'text-primary',
          onClick && 'cursor-pointer text-opacity-80',
          className,
        )}
      >
        {children}
      </Tag>
    );
  },
);

Paragraph.displayName = 'Paragraph';
